// frontend/src/components/reports/Reports.js
// The Reports slide: six reports, each exportable to Excel / PDF / CSV,
// with an on-screen preview of the same data.
import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Grid, Paper, Typography, Button, ButtonGroup, Alert, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Skeleton, Divider, IconButton, Tooltip, TablePagination,
} from '@mui/material';
import {
    PictureAsPdf, Description, TableChart, Visibility, Refresh, Close,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { listReports, getReport, downloadReport } from '../../services/endpoints';

const FORMAT_ICONS = {
    excel: <TableChart fontSize="small" />,
    pdf: <PictureAsPdf fontSize="small" />,
    csv: <Description fontSize="small" />,
};

const Reports = () => {
    const [catalogue, setCatalogue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // preview state
    const [active, setActive] = useState(null);       // report id being previewed
    const [preview, setPreview] = useState(null);     // { title, columns, rows }
    const [previewLoading, setPreviewLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // which (report, format) is downloading
    const [busy, setBusy] = useState(null);

    const loadCatalogue = useCallback(async () => {
        try {
            setLoading(true);
            const res = await listReports();
            setCatalogue(res.data || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadCatalogue(); }, [loadCatalogue]);

    const openPreview = async (report) => {
        setActive(report.id);
        setPreviewLoading(true);
        setPage(0);
        try {
            const res = await getReport(report.id);
            setPreview(res.data);
        } catch (err) {
            toast.error(`Could not load ${report.title}: ${err.response?.data?.message || err.message}`);
            setPreview(null);
            setActive(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleDownload = async (report, format) => {
        const key = `${report.id}:${format}`;
        setBusy(key);
        try {
            await downloadReport(report.id, format, report.title);
            toast.success(`${report.title} exported as ${format.toUpperCase()}`);
        } catch (err) {
            toast.error(`Export failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setBusy(null);
        }
    };

    if (loading) {
        return (
            <Grid container spacing={2}>
                {[...Array(6)].map((_, i) => (
                    <Grid item xs={12} md={6} lg={4} key={i}>
                        <Skeleton variant="rounded" height={170} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    const pagedRows = preview
        ? preview.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : [];

    return (
        <Box>
            <Box sx={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1,
            }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Reports</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Preview on screen, or export to Excel, PDF or CSV.
                    </Typography>
                </Box>
                <Tooltip title="Reload">
                    <IconButton onClick={loadCatalogue}><Refresh /></IconButton>
                </Tooltip>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2}>
                {catalogue.map((report) => (
                    <Grid item xs={12} md={6} lg={4} key={report.id}>
                        <Paper sx={{
                            p: 2, height: '100%', display: 'flex', flexDirection: 'column',
                            border: active === report.id ? 2 : 0,
                            borderColor: 'primary.main',
                        }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                {report.title}
                            </Typography>
                            <Box sx={{ flex: 1, mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Columns
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                    {report.columns.slice(0, 6).map(c => (
                                        <Chip key={c} label={c} size="small" variant="outlined"
                                            sx={{ height: 20, fontSize: 10 }} />
                                    ))}
                                    {report.columns.length > 6 && (
                                        <Chip label={`+${report.columns.length - 6}`} size="small"
                                            sx={{ height: 20, fontSize: 10 }} />
                                    )}
                                </Box>
                            </Box>
                            <Button size="small" startIcon={<Visibility />} sx={{ mb: 1 }}
                                onClick={() => openPreview(report)}>
                                Preview
                            </Button>
                            <ButtonGroup variant="outlined" size="small" fullWidth>
                                {report.formats.map(f => (
                                    <Button key={f}
                                        startIcon={busy === `${report.id}:${f}`
                                            ? <CircularProgress size={14} />
                                            : FORMAT_ICONS[f]}
                                        disabled={!!busy}
                                        onClick={() => handleDownload(report, f)}>
                                        {f === 'excel' ? 'Excel' : f.toUpperCase()}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Preview */}
            {(previewLoading || preview) && (
                <Paper sx={{ mt: 3, p: 2 }}>
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', mb: 1,
                    }}>
                        <Typography variant="h6" fontWeight={600}>
                            {previewLoading ? 'Loading preview...' : preview?.title}
                            {preview && (
                                <Chip label={`${preview.rows.length} rows`} size="small"
                                    sx={{ ml: 1 }} />
                            )}
                        </Typography>
                        <IconButton size="small"
                            onClick={() => { setPreview(null); setActive(null); }}>
                            <Close />
                        </IconButton>
                    </Box>
                    <Divider sx={{ mb: 1 }} />

                    {previewLoading ? (
                        <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : preview?.rows.length === 0 ? (
                        <Alert severity="info">This report currently has no data.</Alert>
                    ) : (
                        <>
                            <TableContainer sx={{ maxHeight: 460 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {preview.columns.map(c => (
                                                <TableCell key={c.key}
                                                    sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                    {c.header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedRows.map((row, i) => (
                                            <TableRow key={i} hover>
                                                {preview.columns.map(c => (
                                                    <TableCell key={c.key} sx={{ whiteSpace: 'nowrap' }}>
                                                        {row[c.key] === null || row[c.key] === undefined
                                                            ? '-' : String(row[c.key])}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={preview.rows.length}
                                page={page}
                                onPageChange={(e, p) => setPage(p)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default Reports;
